import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authAPI } from '../services/api';
import { whatsappUrl } from '../config/whatsapp';
import { goHome } from '../utils/goHome';
import { User, LogOut, Star, ChevronDown, UserCircle2 } from 'lucide-react';
import NotificationBell from './NotificationBell';
import { PASSENGER_NOTIFICATION_TYPES } from '../constants/notificationTypes';

/* ─────────────────────────────────────────────────────────────────────────────
   AppNavbar — dark-blue brand header, identical on every passenger page.
   Desktop center nav is deliberately just المساعدة/حجوزاتي/الرئيسية to match
   the reference homepage design exactly — طرودي/نقل بضائع/مشاوير aren't
   dropped features, just no longer duplicated here: the homepage's own
   service tabs/cards are now the hub for all four services, and the
   mobile hamburger menu still carries the fuller link set.

   Notifications have their own persistent bell control (NotificationBell)
   here — not buried inside حسابي anymore. A brand-new notification still
   interrupts with a popup + sound regardless of page, via
   PassengerNotificationContext; the bell is just where you review the
   list afterward.
───────────────────────────────────────────────────────────────────────────── */
const AppNavbar = () => {
  const navigate   = useNavigate();
  const location   = useLocation();
  const user       = JSON.parse(localStorage.getItem('passenger_user') || '{}');
  const isLoggedIn = !!user?.id;

  const [scrolled,    setScrolled]    = useState(false);
  const [mobileOpen,  setMobileOpen]  = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);

  const accountRef = useRef(null);

  /* scroll shadow */
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  /* close panels on route change */
  useEffect(() => {
    setMobileOpen(false); setAccountOpen(false);
  }, [location.pathname]);

  /* outside-click close */
  useEffect(() => {
    const fn = (e) => {
      if (accountRef.current && !accountRef.current.contains(e.target)) setAccountOpen(false);
    };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, []);

  const logout = async () => {
    try { await authAPI.logout(); } catch { /* session may already be expired */ }
    localStorage.removeItem('passenger_user');
    navigate('/');
  };


  const isHome      = location.pathname === '/' || location.pathname === '/search';
  const isBookings  = location.pathname === '/bookings';
  const isParcels   = location.pathname.startsWith('/parcels');
  const isTransport = location.pathname.startsWith('/transport');
  const isRides      = location.pathname.startsWith('/rides');

  /* desktop nav link */
  const DNavLink = ({ active, onClick, children }) => (
    <button
      onClick={onClick}
      className={`relative px-1 py-1 text-[14px] font-semibold transition-all duration-150 whitespace-nowrap ${
        active ? 'text-white' : 'text-white/90 hover:text-white'
      }`}
    >
      {children}
      <span className={`absolute bottom-0 right-0 left-0 h-0.5 rounded-full transition-all duration-200 ${
        active ? 'bg-[#FFB800] opacity-100' : 'opacity-0'
      }`} />
    </button>
  );

  /* mobile nav link */
  const MNavLink = ({ active, onClick, children }) => (
    <button
      onClick={onClick}
      className={`w-full text-right px-4 py-3 rounded-xl text-[15px] font-semibold transition-all ${
        active ? 'text-white bg-white/15' : 'text-white/90 hover:text-white hover:bg-white/10'
      }`}
    >
      {children}
    </button>
  );

  return (
    <>
      {/* ── Fixed navbar ── */}
      <nav
        dir="rtl"
        className={`fixed top-0 inset-x-0 z-50 h-[68px] transition-all duration-200 ${
          scrolled
            ? 'shadow-[0_4px_24px_rgba(30,58,138,.45)]'
            : 'border-b border-white/10'
        }`}
        style={{ background: 'linear-gradient(90deg, #0F2354 0%, #16307A 35%, #2F52B8 72%, #5D82E8 100%)' }}
      >
        <div className="max-w-6xl mx-auto h-full flex items-center justify-between gap-4 px-4 sm:px-6">

          {/* ── Logo + tagline — real HTML text, not a bitmap ──
               Every logo file supplied so far is an opaque 3D badge with a
               filled rectangular/oval background baked in — placed on the
               navbar it reads as "an image pasted onto the header" instead
               of an integrated wordmark, and none of them have a
               transparent version to crop from (background-removal on this
               art style was already tried and abandoned once this session —
               it ate into the badge's own gradient fill). A plain text
               wordmark is transparent by construction, crisper at any size,
               and matches the tagline below it (already real text, not
               baked into an image either). The small dot echoes the gold
               dot present in every supplied logo variant. */}
          <button
            onClick={() => goHome(navigate, location.pathname)}
            className="flex flex-col items-end flex-shrink-0 group leading-none"
          >
            <span className="relative inline-block group-hover:scale-105 transition-transform">
              <span className="text-[26px] sm:text-[30px] font-black text-white tracking-tight">سافر</span>
              <span className="absolute -top-1 right-1.5 w-[5px] h-[5px] rounded-full bg-[#FFC928]" aria-hidden="true" />
            </span>
            <span className="text-[9px] sm:text-[11px] font-semibold text-white/80 mt-0.5 whitespace-nowrap">معك في كل طريق</span>
          </button>

          {/* ── Desktop center nav — مطابق للتصميم: المساعدة / حجوزاتي / الرئيسية فقط.
               باقي الخدمات (طرودي/نقل بضائع/مشاوير) متاحة من الصفحة الرئيسية نفسها
               عبر تبويبات وبطاقات الخدمات، ومن قائمة الموبايل أدناه. ── */}
          <div className="hidden md:flex items-center gap-7">
            {/* DOM order matters here: in an RTL flex row the first child
                renders rightmost (closest to the logo), so الرئيسية goes
                first to match the reference — تسجيل الدخول anchors the
                far (left) end, closest to the login button. */}
            <DNavLink active={isHome}     onClick={() => goHome(navigate, location.pathname)}>الرئيسية</DNavLink>
            <DNavLink active={isBookings} onClick={() => navigate('/bookings')}>حجوزاتي</DNavLink>
            {/* Help → WhatsApp directly */}
            <a
              href={whatsappUrl()}
              target="_blank"
              rel="noopener noreferrer"
              className="relative px-1 py-1 text-[14px] font-semibold text-white/90 hover:text-white transition-all whitespace-nowrap flex items-center gap-1.5"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" className="text-green-400 flex-shrink-0">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              المساعدة
            </a>
          </div>

          {/* ── Right controls ── */}
          <div className="flex items-center gap-1.5">

            {isLoggedIn && <div className="hidden md:block"><NotificationBell dark types={PASSENGER_NOTIFICATION_TYPES} /></div>}

            {/* Account control / Login button — desktop only when logged
                in; on mobile this collapses into the hamburger drawer
                below instead of sitting as its own icon next to it.
                Logged-in state is a labeled chip (avatar + name + chevron)
                rather than a bare initial-letter circle — a single letter
                with no other context doesn't read as "this is your
                account" at a glance, especially for a first-time or
                returning user who doesn't already know what to expect. */}
            {isLoggedIn ? (
              <div className="hidden md:block relative" ref={accountRef}>
                <button
                  onClick={() => setAccountOpen(o => !o)}
                  className="relative flex items-center gap-2 pr-3 pl-2 py-1.5 rounded-full bg-white/15 hover:bg-white/25 text-white transition-all max-w-[180px]"
                >
                  <span className="relative w-7 h-7 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 font-bold text-[12px]">
                    {user.name?.charAt(0) || <User size={14} strokeWidth={2} />}
                  </span>
                  <span className="text-[13px] font-semibold truncate">{user.name?.split(' ')[0] || 'حسابي'}</span>
                  <ChevronDown size={14} strokeWidth={2.5} className="flex-shrink-0 opacity-80" />
                </button>

                {accountOpen && (
                  <div dir="rtl" className="absolute top-full mt-2 left-0 w-52 bg-white rounded-2xl shadow-[0_12px_40px_rgba(15,36,96,.2)] border border-gray-100 z-50 overflow-hidden animate-slide-down">
                    <div className="px-4 py-3 bg-gradient-to-br from-[#1E3A8A] to-[#1E40AF] border-b border-blue-900/20">
                      <p className="text-sm font-bold text-white truncate">{user.name || 'مسافر'}</p>
                      {user.loyaltyPoints > 0 && (
                        <p className="text-xs text-yellow-300 mt-0.5 flex items-center gap-1">
                          <Star size={12} strokeWidth={2} className="fill-yellow-300" /> {user.loyaltyPoints} نقطة
                        </p>
                      )}
                    </div>
                    <button onClick={() => { setAccountOpen(false); navigate('/profile'); }}
                      className="w-full text-right px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 transition-colors border-b border-gray-50">
                      <UserCircle2 size={16} strokeWidth={2} />
                      <span className="flex-1">حسابي</span>
                    </button>
                    <button onClick={() => { setAccountOpen(false); logout(); }}
                      className="w-full text-right px-4 py-3 text-sm text-red-600 hover:bg-red-50 flex items-center gap-3 transition-colors">
                      <LogOut size={15} strokeWidth={2} />
                      <span>تسجيل الخروج</span>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button
                onClick={() => navigate('/login')}
                className="hidden md:flex items-center gap-2 bg-white hover:bg-gray-50 text-[#0F2354] text-[13px] font-bold pr-4 pl-1.5 py-1.5 rounded-full shadow-sm transition-all"
              >
                تسجيل الدخول
                <span className="w-7 h-7 rounded-full bg-[#0F2354] flex items-center justify-center flex-shrink-0">
                  <User size={13} strokeWidth={2} className="text-white" />
                </span>
              </button>
            )}

            {isLoggedIn && <div className="md:hidden"><NotificationBell dark types={PASSENGER_NOTIFICATION_TYPES} /></div>}

            {/* Hamburger — mobile only */}
            <button
              onClick={() => setMobileOpen(o => !o)}
              className="md:hidden w-10 h-10 flex items-center justify-center rounded-xl text-white/90 hover:bg-white/10 transition-all"
              aria-label="القائمة"
            >
              {mobileOpen ? (
                <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              ) : (
                <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16"/>
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* ── Mobile slide-down menu ── */}
        {mobileOpen && (
          <div dir="rtl" className="md:hidden absolute top-full inset-x-0 bg-[#1E3A8A] border-b border-white/10 shadow-[0_8px_24px_rgba(30,58,138,.35)] px-4 py-3 animate-slide-down">
            {/* الرئيسية/حجوزاتي/المساعدة removed here — they're already one
                tap away in the always-visible bottom nav bar on mobile, so
                repeating them in this drawer was pure duplication. What's
                left is genuinely unique to this menu: service shortcuts not
                already on the bottom bar, plus the footer-info link. */}
            <nav className="flex flex-col gap-1 mb-3">
              <MNavLink active={isParcels}  onClick={() => navigate('/parcels')}>طرودي</MNavLink>
              <MNavLink active={isTransport} onClick={() => navigate('/transport')}>نقل بضائع</MNavLink>
              <MNavLink active={isRides} onClick={() => navigate('/rides')}>مشاوير</MNavLink>
              <MNavLink active={false} onClick={() => { setMobileOpen(false); setTimeout(() => document.getElementById('footer-info')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150); }}>المعلومات</MNavLink>
            </nav>
            <div className="h-px bg-white/10 mb-3" />
            {!isLoggedIn ? (
              <button onClick={() => navigate('/login')}
                className="w-full py-3 flex items-center justify-center gap-2 border border-white/30 hover:bg-white/10 text-white text-[14px] font-semibold rounded-xl transition-all">
                <User size={15} strokeWidth={2} />
                تسجيل الدخول
              </button>
            ) : (
              <>
                {/* Profile info — tappable through to الملف الشخصي;
                    notifications now live in the standalone bell above,
                    not here. */}
                <button
                  onClick={() => { setMobileOpen(false); navigate('/profile'); }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 mb-1 rounded-xl bg-white/5 hover:bg-white/10 transition-colors"
                >
                  <span className="relative w-9 h-9 flex-shrink-0 rounded-xl bg-white/15 flex items-center justify-center text-white font-bold text-[13px]">
                    {user.name?.charAt(0) || <User size={16} strokeWidth={2} />}
                  </span>
                  <div className="min-w-0 flex-1 text-right">
                    <p className="text-sm font-bold text-white truncate">{user.name || 'مسافر'}</p>
                    {user.loyaltyPoints > 0 && (
                      <p className="text-xs text-yellow-300 flex items-center gap-1">
                        <Star size={11} strokeWidth={2} className="fill-yellow-300" /> {user.loyaltyPoints} نقطة
                      </p>
                    )}
                  </div>
                </button>
                <button onClick={logout}
                  className="w-full py-3 text-center text-red-300 hover:bg-white/10 text-[14px] font-semibold rounded-xl transition-colors">
                  تسجيل الخروج
                </button>
              </>
            )}
          </div>
        )}
      </nav>

      {/* spacer so fixed nav doesn't overlap content */}
      <div className="h-[68px]" aria-hidden="true" />
    </>
  );
};

export default AppNavbar;
